import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { Col, Form, Modal, Row } from "react-bootstrap";
import {
  getSession,
  handleError,
  requestSquematic,
  updateSessionSaldo,
} from "../constants/commonFunctionts";

const PAIS_ID = "169";
const BOGOTA_DEPTO_ID = "11";
const BOGOTA_CIUDAD_ID = "1";

const esBogota = (departamentoId, ciudadId) =>
  departamentoId === BOGOTA_DEPTO_ID && ciudadId === BOGOTA_CIUDAD_ID;

const PRECIO_PAQUETE = {
  Pequeño: 7000,
  Mediano: 11000,
  Grande: 18000,
};

const RECARGO_BARRIO_DISTINTO = 15000;
const RECARGO_MISMO_BARRIO = 5000;

const formatoCOP = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 0,
});

const crearPaqueteVacio = () => ({
  tamano: "",
  delicado: false,
});

const crearPedidoVacio = () => ({
  c901_departamento_recogida: "",
  c901_ciudad_recogida: "",
  c901_barrio_recogida: "",
  c901_dir_recogida: "",
  c901_departamento_entrega: "",
  c901_ciudad_entrega: "",
  c901_barrio_entrega: "",
  c901_dir_entrega: "",
  c901_conf_pedido: [crearPaqueteVacio()],
  c901_fecha_recogida: "",
});

const crearUbicacionVacia = () => ({
  departamentoId: "",
  ciudadId: "",
  localidadId: "",
  barrioId: "",
  ciudades: [],
  localidades: [],
  barrios: [],
});

const crearCargaVacia = () => ({
  ciudades: false,
  localidades: false,
  barrios: false,
});

const CAMPOS_TEXTO_PEDIDO = [
  "c901_departamento_recogida",
  "c901_ciudad_recogida",
  "c901_barrio_recogida",
  "c901_dir_recogida",
  "c901_departamento_entrega",
  "c901_ciudad_entrega",
  "c901_barrio_entrega",
  "c901_dir_entrega",
  "c901_fecha_recogida",
];

const CAMPOS_POR_TIPO = {
  recogida: {
    departamento: "c901_departamento_recogida",
    ciudad: "c901_ciudad_recogida",
    barrio: "c901_barrio_recogida",
    direccion: "c901_dir_recogida",
  },
  entrega: {
    departamento: "c901_departamento_entrega",
    ciudad: "c901_ciudad_entrega",
    barrio: "c901_barrio_entrega",
    direccion: "c901_dir_entrega",
  },
};

const CampoTexto = ({ label, type = "text", value, onChange, disabled }) => (
  <Form.Group className="mb-2">
    <Form.Label>{label}</Form.Label>
    <Form.Control
      type={type}
      value={value}
      onChange={onChange}
      disabled={disabled}
    />
  </Form.Group>
);

const CampoSelect = ({
  label,
  value,
  onChange,
  disabled,
  cargando,
  opciones,
  placeholder,
}) => (
  <Form.Group className="mb-2">
    <Form.Label
      style={{ display: "flex", alignItems: "center", gap: 8 }}
    >
      {label}
      {cargando && <CircularProgress size={12} color="inherit" />}
    </Form.Label>
    <Form.Select
      value={value}
      onChange={onChange}
      disabled={disabled || cargando}
    >
      <option value="">{cargando ? "Cargando..." : placeholder}</option>
      {opciones.map((opcion) => (
        <option key={opcion.id} value={opcion.id}>
          {opcion.value}
        </option>
      ))}
    </Form.Select>
  </Form.Group>
);

export default function NuevoPedidoModal({ show, onClose, onSave }) {
  const session = getSession();
  const [pedido, setPedido] = useState(crearPedidoVacio());
  const [departmentList, setDepartmentList] = useState([]);
  const [cargandoDepartamentos, setCargandoDepartamentos] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState({
    recogida: crearCargaVacia(),
    entrega: crearCargaVacia(),
  });
  const [ubicacion, setUbicacion] = useState({
    recogida: crearUbicacionVacia(),
    entrega: crearUbicacionVacia(),
  });

  useEffect(() => {
    if (show) {
      setPedido(crearPedidoVacio());
      setUbicacion({
        recogida: crearUbicacionVacia(),
        entrega: crearUbicacionVacia(),
      });
      fetchDepartments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const recogidaCompleta =
    pedido.c901_departamento_recogida !== "" &&
    pedido.c901_ciudad_recogida !== "" &&
    pedido.c901_barrio_recogida !== "" &&
    pedido.c901_dir_recogida.trim() !== "";

  const entregaUbicacionCompleta =
    pedido.c901_departamento_entrega !== "" &&
    pedido.c901_ciudad_entrega !== "" &&
    pedido.c901_barrio_entrega !== "";

  const ubicacionCompleta = recogidaCompleta && entregaUbicacionCompleta;

  const ciudadesDistintas =
    ubicacionCompleta &&
    (ubicacion.recogida.departamentoId !== ubicacion.entrega.departamentoId ||
      ubicacion.recogida.ciudadId !== ubicacion.entrega.ciudadId);

  const isPedidoValido =
    CAMPOS_TEXTO_PEDIDO.every((campo) => pedido[campo].trim() !== "") &&
    pedido.c901_conf_pedido.length > 0 &&
    pedido.c901_conf_pedido.every((paquete) => paquete.tamano !== "") &&
    !ciudadesDistintas;

  const costoPaquetes = pedido.c901_conf_pedido.reduce(
    (total, paquete) => total + (PRECIO_PAQUETE[paquete.tamano] || 0),
    0
  );

  let costoEnvio = 0;
  if (ubicacionCompleta && !ciudadesDistintas) {
    const mismoBarrio =
      ubicacion.recogida.barrioId === ubicacion.entrega.barrioId;
    costoEnvio = mismoBarrio ? RECARGO_MISMO_BARRIO : RECARGO_BARRIO_DISTINTO;
  }

  const costoTotal = costoPaquetes + costoEnvio;

  const algoCargando =
    cargandoDepartamentos ||
    Object.values(cargando.recogida).some(Boolean) ||
    Object.values(cargando.entrega).some(Boolean);

  const marcarCargando = (tipo, nivel, valor) => {
    setCargando((prev) => ({
      ...prev,
      [tipo]: { ...prev[tipo], [nivel]: valor },
    }));
  };

  const fetchDepartments = async () => {
    setCargandoDepartamentos(true);
    try {
      const res = await requestSquematic(
        "GET",
        "/api/panel/geographic_location/get_departments",
        { c102_id_pais: PAIS_ID },
        session?.token
      );

      if (res && res.results) {
        setDepartmentList(
          res.results
            .map((departamento) => ({
              id: departamento.c102_id,
              value: departamento.c102_descripcion,
            }))
            .sort((a, b) => a.value.localeCompare(b.value))
        );
      } else if (res && res.errors) {
        throw new Error(res.errors);
      }
    } catch (error) {
      handleError("Error obteniendo los departamentos", error);
    } finally {
      setCargandoDepartamentos(false);
    }
  };

  const fetchCities = async (tipo, departamentoId) => {
    if (!departamentoId) return;
    marcarCargando(tipo, "ciudades", true);
    try {
      const res = await requestSquematic(
        "GET",
        "/api/panel/geographic_location/get_cities",
        { c103_id_pais: PAIS_ID, c103_id_depto: departamentoId },
        session?.token
      );

      if (res && res.results) {
        const ciudades = res.results.map((ciudad) => ({
          id: ciudad.c103_id,
          value: ciudad.c103_descripcion,
        }));
        setUbicacion((prev) => ({
          ...prev,
          [tipo]: { ...prev[tipo], ciudades },
        }));
      } else if (res && res.errors) {
        throw new Error(res.errors);
      }
    } catch (error) {
      handleError(
        "Error obteniendo las ciudades del departamento " + departamentoId,
        error
      );
    } finally {
      marcarCargando(tipo, "ciudades", false);
    }
  };

  const fetchLocalities = async (tipo, departamentoId, ciudadId) => {
    marcarCargando(tipo, "localidades", true);
    try {
      const res = await requestSquematic(
        "GET",
        "/api/panel/locality/get_by_location",
        {
          c108_id_pais: PAIS_ID,
          c108_id_depto: departamentoId,
          c108_id_ciudad: ciudadId,
        },
        session?.token
      );

      if (res && res.results) {
        const localidades = res.results.map((localidad) => ({
          id: localidad.c106_rowid,
          value: localidad.c106_descripcion,
          neighs: (localidad.communes_by_locality || [])
            .flatMap((c) => c.commune?.neighborhoods_by_commune || [])
            .map((n) => n.neighborhood)
            .filter(Boolean)
            .map((n) => ({ id: n.c104_id, value: n.c104_descripcion })),
        }));
        setUbicacion((prev) => ({
          ...prev,
          [tipo]: { ...prev[tipo], localidades },
        }));
      } else if (res && res.errors) {
        throw new Error(res.errors);
      }
    } catch (error) {
      handleError(
        "Error obteniendo las localidades de la ciudad " + ciudadId,
        error
      );
    } finally {
      marcarCargando(tipo, "localidades", false);
    }
  };

  const fetchBarrios = async (tipo, departamentoId, ciudadId) => {
    marcarCargando(tipo, "barrios", true);
    try {
      const res = await requestSquematic(
        "GET",
        "/api/panel/neighborhood/get_by_location",
        {
          c104_id_pais: PAIS_ID,
          c104_id_depto: departamentoId,
          c104_id_ciudad: ciudadId,
        },
        session?.token
      );

      if (res && res.results) {
        const barrios = res.results
          .filter(
            (item) => item && (item.c104_ind_estado + "").trim() === "1"
          )
          .map((item) => ({
            id: item.c104_id,
            value: item.c104_descripcion,
          }));
        setUbicacion((prev) => ({
          ...prev,
          [tipo]: { ...prev[tipo], barrios },
        }));
      } else if (res && res.errors) {
        throw new Error(res.errors);
      }
    } catch (error) {
      handleError(
        "Error obteniendo los barrios del departamento " +
        departamentoId +
        " y la ciudad " +
        ciudadId,
        error
      );
    } finally {
      marcarCargando(tipo, "barrios", false);
    }
  };

  const handleCampoChange = (campo, valor) => {
    setPedido((prev) => ({ ...prev, [campo]: valor }));
  };

  const handleDepartamentoChange = (tipo, departamentoId) => {
    const departamento = departmentList.find(
      (d) => String(d.id) === departamentoId
    );
    const campos = CAMPOS_POR_TIPO[tipo];

    setPedido((prev) => ({
      ...prev,
      [campos.departamento]: departamento ? departamento.value : "",
      [campos.ciudad]: "",
      [campos.barrio]: "",
    }));

    setUbicacion((prev) => ({
      ...prev,
      [tipo]: { ...crearUbicacionVacia(), departamentoId },
    }));

    if (departamentoId) {
      fetchCities(tipo, departamentoId);
    }
  };

  const handleCiudadChange = (tipo, ciudadId) => {
    const ciudad = ubicacion[tipo].ciudades.find(
      (c) => String(c.id) === ciudadId
    );
    const campos = CAMPOS_POR_TIPO[tipo];
    const departamentoId = ubicacion[tipo].departamentoId;

    setPedido((prev) => ({
      ...prev,
      [campos.ciudad]: ciudad ? ciudad.value : "",
      [campos.barrio]: "",
    }));

    setUbicacion((prev) => ({
      ...prev,
      [tipo]: {
        ...prev[tipo],
        ciudadId,
        localidadId: "",
        localidades: [],
        barrioId: "",
        barrios: [],
      },
    }));

    if (!ciudadId) return;

    if (esBogota(departamentoId, ciudadId)) {
      fetchLocalities(tipo, departamentoId, ciudadId);
    } else {
      fetchBarrios(tipo, departamentoId, ciudadId);
    }
  };

  const handleLocalidadChange = (tipo, localidadId) => {
    const localidad = ubicacion[tipo].localidades.find(
      (l) => String(l.id) === localidadId
    );
    const campos = CAMPOS_POR_TIPO[tipo];

    setPedido((prev) => ({ ...prev, [campos.barrio]: "" }));

    setUbicacion((prev) => ({
      ...prev,
      [tipo]: {
        ...prev[tipo],
        localidadId,
        barrioId: "",
        barrios: localidad ? localidad.neighs : [],
      },
    }));
  };

  const handleBarrioChange = (tipo, barrioId) => {
    const barrio = ubicacion[tipo].barrios.find(
      (b) => String(b.id) === barrioId
    );
    const campos = CAMPOS_POR_TIPO[tipo];

    setPedido((prev) => ({
      ...prev,
      [campos.barrio]: barrio ? barrio.value : "",
    }));

    setUbicacion((prev) => ({
      ...prev,
      [tipo]: { ...prev[tipo], barrioId },
    }));
  };

  const handleAddPaquete = () => {
    setPedido((prev) => ({
      ...prev,
      c901_conf_pedido: [...prev.c901_conf_pedido, crearPaqueteVacio()],
    }));
  };

  const handlePaqueteChange = (index, campo, valor) => {
    setPedido((prev) => ({
      ...prev,
      c901_conf_pedido: prev.c901_conf_pedido.map((paquete, i) =>
        i === index ? { ...paquete, [campo]: valor } : paquete
      ),
    }));
  };

  const handleRemovePaquete = (index) => {
    setPedido((prev) => ({
      ...prev,
      c901_conf_pedido: prev.c901_conf_pedido.filter((_, i) => i !== index),
    }));
  };

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      const payload = {
        ...pedido,
        c901_valor: costoTotal,
        c901_conf_pedido: JSON.stringify(pedido.c901_conf_pedido),
      };

      const res = await requestSquematic(
        "POST",
        "/api/resuelve/registrar_pedido",
        payload,
        session?.token
      );

      if (res && res.results) {
        updateSessionSaldo(res.results.nuevo_saldo);
        onSave(pedido);
      } else if (res && res.errors) {
        throw new Error(res.errors);
      }
    } catch (error) {
      handleError("Error guardando el pedido", error);
    } finally {
      setGuardando(false);
    }
  };

  const renderSeccionUbicacion = (tipo, extraDisabled = false) => {
    const datos = ubicacion[tipo];
    const cargas = cargando[tipo];
    const campos = CAMPOS_POR_TIPO[tipo];
    const mostrarLocalidad = esBogota(datos.departamentoId, datos.ciudadId);

    return (
      <>
        <Row className="mb-3">
          <Col md={6}>
            <CampoSelect
              label="Departamento"
              placeholder="Selecciona un departamento"
              value={datos.departamentoId}
              opciones={departmentList}
              disabled={extraDisabled}
              cargando={cargandoDepartamentos}
              onChange={(e) => handleDepartamentoChange(tipo, e.target.value)}
            />
          </Col>
          <Col md={6}>
            <CampoSelect
              label="Ciudad"
              placeholder="Selecciona una ciudad"
              value={datos.ciudadId}
              opciones={datos.ciudades}
              disabled={extraDisabled || !datos.departamentoId}
              cargando={cargas.ciudades}
              onChange={(e) => handleCiudadChange(tipo, e.target.value)}
            />
          </Col>
          {mostrarLocalidad && (
            <Col md={6}>
              <CampoSelect
                label="Localidad"
                placeholder="Selecciona una localidad"
                value={datos.localidadId}
                opciones={datos.localidades}
                disabled={extraDisabled}
                cargando={cargas.localidades}
                onChange={(e) => handleLocalidadChange(tipo, e.target.value)}
              />
            </Col>
          )}
          <Col md={6}>
            <CampoSelect
              label="Barrio"
              placeholder="Selecciona un barrio"
              value={datos.barrioId}
              opciones={datos.barrios}
              disabled={
                extraDisabled ||
                !datos.ciudadId ||
                (mostrarLocalidad && !datos.localidadId)
              }
              cargando={cargas.barrios}
              onChange={(e) => handleBarrioChange(tipo, e.target.value)}
            />
          </Col>
          <Col md={6}>
            <CampoTexto
              label="Dirección"
              value={pedido[campos.direccion]}
              disabled={extraDisabled}
              onChange={(e) =>
                handleCampoChange(campos.direccion, e.target.value)
              }
            />
          </Col>
        </Row>
        {tipo === "entrega" && ciudadesDistintas && (
          <Typography sx={{ fontSize: 13, color: "#dc3545", fontWeight: "bold" }} mb={2}>
            Por el momento no realizamos envíos intermunicipales. Selecciona la
            misma ciudad de recogida y entrega.
          </Typography>
        )}
      </>
    );
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Nuevo pedido</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Typography
          sx={{ fontSize: 14, fontWeight: "bold", color: "#797979" }}
          mb={1}
        >
          Recogida
        </Typography>
        {renderSeccionUbicacion("recogida")}

        <Typography
          sx={{ fontSize: 14, fontWeight: "bold", color: "#797979" }}
          mb={1}
        >
          Entrega
        </Typography>
        {!recogidaCompleta && (
          <Typography sx={{ fontSize: 12, color: "#e2574c" }} mb={1}>
            Completa primero la información de recogida
          </Typography>
        )}
        {renderSeccionUbicacion("entrega", !recogidaCompleta)}

        <Row className="mb-3">
          <Col md={6}>
            <CampoTexto
              label="Fecha y hora de recogida"
              type="datetime-local"
              value={pedido.c901_fecha_recogida}
              onChange={(e) =>
                handleCampoChange("c901_fecha_recogida", e.target.value)
              }
            />
          </Col>
        </Row>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          mb={1}
        >
          <Typography sx={{ fontSize: 14, fontWeight: "bold", color: "#797979" }}>
            Paquetes
          </Typography>
          <button
            className="btn"
            style={{
              backgroundColor: "#11ddb3",
              color: "white",
              borderRadius: 8,
              fontWeight: "bold",
              padding: "6px 14px",
              fontSize: 13,
            }}
            onClick={handleAddPaquete}
          >
            + Añadir paquete
          </button>
        </Box>

        {pedido.c901_conf_pedido.map((paquete, index) => (
          <Row key={index} className="mb-2">
            <Col md={6} className="mb-2">
              <Form.Group>
                <Form.Label>Tamaño del paquete #{index + 1}</Form.Label>
                <Form.Select
                  value={paquete.tamano}
                  onChange={(e) =>
                    handlePaqueteChange(index, "tamano", e.target.value)
                  }
                >
                  <option value="">Selecciona un tamaño</option>
                  <option value="Pequeño">Pequeño</option>
                  <option value="Mediano">Mediano</option>
                  <option value="Grande">Grande</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col
              md={3}
              className="mb-2 d-flex align-items-center justify-content-center"
            >
              <Form.Check
                type="checkbox"
                id={`paquete-delicado-${index}`}
                label="¿Es delicado?"
                checked={paquete.delicado}
                onChange={(e) =>
                  handlePaqueteChange(index, "delicado", e.target.checked)
                }
              />
            </Col>
            <Col
              md={3}
              className="mb-2 d-flex align-items-center justify-content-center"
            >
              {pedido.c901_conf_pedido.length > 1 && (
                <button
                  className="btn"
                  style={{
                    backgroundColor: "#f5c6cb",
                    color: "#842029",
                    borderRadius: 8,
                    fontWeight: "bold",
                    padding: "6px 14px",
                    fontSize: 13,
                  }}
                  onClick={() => handleRemovePaquete(index)}
                >
                  Eliminar
                </button>
              )}
            </Col>
          </Row>
        ))}

        <Box
          sx={{
            backgroundColor: "#f0f4ff",
            borderRadius: 2,
            padding: "16px 20px",
          }}
          mt={3}
        >
          <Typography sx={{ fontSize: 14, color: "#797979" }}>
            Costo de paquetes: {formatoCOP.format(costoPaquetes)}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#797979" }}>
            Costo de envío:{" "}
            {!ubicacionCompleta
              ? "completa la ubicación de recogida y entrega"
              : ciudadesDistintas
                ? "no disponible (envío intermunicipal)"
                : formatoCOP.format(costoEnvio)}
          </Typography>
          <Typography
            sx={{ fontSize: 20, fontWeight: "bold", color: "#1555fd" }}
            mt={1}
          >
            Total a pagar: {formatoCOP.format(costoTotal)}
          </Typography>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <button
          className="btn"
          style={{
            backgroundColor: "#e0e0e0",
            color: "#333",
            borderRadius: 8,
            fontWeight: "bold",
            padding: "10px 20px",
          }}
          onClick={onClose}
        >
          Cancelar
        </button>
        <button
          className="btn"
          style={{
            backgroundColor: "#1555fd",
            color: "white",
            borderRadius: 8,
            fontWeight: "bold",
            padding: "10px 20px",
          }}
          disabled={!isPedidoValido || algoCargando || guardando}
          onClick={handleGuardar}
        >
          {guardando ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Guardar"
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
